import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';

// Define a more specific type for the questionnaire data items to be displayed
interface DisplayItem {
  label: string;
  value: string | string[] | undefined | null;
}

const ConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null); // For any errors during final submission

  const { questionnaireData, email, password } = location.state || {};

  useEffect(() => {
    console.log('ConfirmationPage rendered. Data received:', { questionnaireData, email }); // Don't log password
    if (!questionnaireData || !email || !password) {
      console.warn('Confirmation page loaded without complete data, redirecting to account creation.');
      navigate('/account-creation'); 
    }
  }, [questionnaireData, email, password, navigate]);

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    setError(null);

    const finalUserData = {
      ...questionnaireData,
      email,
      // Password would be sent to a backend here, ideally already hashed or to be hashed by the server.
      // For this simulation, we just include it in the log.
    };
    
    // Log the password separately only in this dev environment simulation if absolutely necessary for debugging,
    // but generally avoid logging sensitive data.
    console.log('Simulating FINAL account creation with data:', finalUserData, 'Password (simulated):', password);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsLoading(false);

    // On successful simulated creation:
    console.log('Account created successfully (simulated) for:', email);
    
    // Navigate to student profile page with the data
    navigate('/student-profile', { 
      state: { 
        studentData: finalUserData 
      } 
    });
  };

  if (!questionnaireData || !email) {
    // Fallback if data is missing, though useEffect should redirect
    return (
      <MinimalLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p>Loading confirmation details or redirecting...</p>
        </div>
      </MinimalLayout>
    );
  }

  // Prepare data for display
  const displayItems: DisplayItem[] = [
    { label: 'Email', value: email },
    ...(questionnaireData.learnerFirstName ? [{ label: 'Learner Name', value: questionnaireData.learnerFirstName }] : []),
    { label: 'Instrument', value: questionnaireData.instrument },
    { label: 'Age', value: questionnaireData.age },
    { label: 'Goal', value: questionnaireData.goals }, // Assuming goals is a single string now based on QuestionnaireData
    { label: 'Skill Level', value: questionnaireData.skillLevel }, 
    { label: 'Genres', value: questionnaireData.genres?.join(', ') },
    { label: 'Previous Lessons', value: questionnaireData.previousLessons },
    { label: 'Lesson Frequency', value: questionnaireData.lessonFrequency },
    { label: 'Lesson Location(s)', value: questionnaireData.lessonLocation?.join(', ') },
  ].filter(item => item.value !== null && item.value !== undefined && item.value !== ''); // Filter out empty/null values

  return (
    <MinimalLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col items-center justify-center p-4 pt-20">
        <div className="w-full max-w-lg bg-white p-8 sm:p-10 rounded-xl shadow-2xl">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-8">
            Confirm Your Details
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Please review your information below before completing your account creation.
          </p>

          <div className="space-y-4 mb-8 p-6 bg-blue-50 rounded-lg">
            {displayItems.map((item, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-center">
                <p className="font-semibold text-blue-800 sm:w-1/3">{item.label}:</p>
                <p className="text-gray-700 sm:w-2/3 break-words">
                  {Array.isArray(item.value) ? item.value.join(', ') : item.value}
                </p>
              </div>
            ))}
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-100 p-3 rounded-md mb-4">{error}</p>
          )}

          <div className="space-y-4">
            <button
              onClick={handleFinalSubmit}
              disabled={isLoading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-full shadow-lg text-lg font-semibold text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-all transform hover:-translate-y-0.5"
            >
              {isLoading ? 'Creating Account...' : 'Confirm & Create Account'}
            </button>
            <button
              onClick={() => navigate('/account-creation', { state: { questionnaireData, email, password } })}
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-full shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
            >
              Edit Information
            </button>
          </div>
          <p className="mt-8 text-xs text-gray-500 text-center">
            By creating an account, you agree to our (not yet existing) Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default ConfirmationPage; 